import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { UserPlus, Check, X, Search } from "lucide-react";

export default function Friends() {
  const [items, setItems] = useState([]);
  const [results, setResults] = useState([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/friends");
      setItems(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    const { data } = await api.get("/users", { params: { q } });
    setResults(data);
  };

  const sendRequest = async (uid) => {
    try {
      await api.post("/friends/request", { addressee_id: uid });
      await load();
      setResults([]);
      setQ("");
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const accept = async (id) => { await api.post(`/friends/${id}/accept`); load(); };
  const remove = async (id) => { await api.delete(`/friends/${id}`); load(); };

  const accepted = items.filter((i) => i.status === "accepted");
  const incoming = items.filter((i) => i.status === "pending" && i.direction === "incoming");
  const outgoing = items.filter((i) => i.status === "pending" && i.direction === "outgoing");

  return (
    <div className="space-y-8 fade-in" data-testid="friends-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Freunde</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">Mein Netzwerk</h1>
        <p className="text-white/60 text-sm mt-2">Verbinde dich mit anderen Nordgaming-Mitgliedern.</p>
      </div>

      <form onSubmit={search} className="card-solid p-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Mitglied suchen (Name oder E-Mail)…"
            className="input pl-10"
            data-testid="friends-search-input"
          />
        </div>
        <button type="submit" className="btn-secondary" data-testid="friends-search-submit">Suchen</button>
      </form>

      {results.length > 0 && (
        <div className="card-solid divide-y divide-white/5">
          {results.map((u) => {
            const existing = items.find((i) => i.peer_id === u.user_id);
            return (
              <div key={u.user_id} className="p-3 flex items-center gap-3" data-testid={`search-result-${u.user_id}`}>
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-8 h-8 object-cover rounded-sm border border-white/10" />
                ) : (
                  <div className="w-8 h-8 bg-neon-gradient-soft border border-primary/30 text-primary flex items-center justify-center font-mono text-xs rounded-sm">
                    {u.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{u.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{u.member_id}</div>
                </div>
                {existing ? (
                  <span className="chip text-white/40">{existing.status}</span>
                ) : (
                  <button onClick={() => sendRequest(u.user_id)} className="btn-secondary !py-1.5" data-testid={`add-friend-${u.user_id}`}>
                    <UserPlus className="w-3.5 h-3.5" /> Anfragen
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {incoming.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-magenta mb-3">Eingehende Anfragen</div>
          <div className="card-solid divide-y divide-white/5">
            {incoming.map((f) => (
              <div key={f.id} className="p-3 flex items-center gap-3" data-testid={`incoming-${f.id}`}>
                <FriendAvatar u={f} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{f.peer_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{f.peer_member_id}</div>
                </div>
                <button onClick={() => accept(f.id)} className="btn-primary !py-1.5" data-testid={`accept-${f.id}`}>
                  <Check className="w-3.5 h-3.5" /> Akzeptieren
                </button>
                <button onClick={() => remove(f.id)} className="btn-ghost !p-2 text-red-400" data-testid={`reject-${f.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {outgoing.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Ausgehende Anfragen</div>
          <div className="card-solid divide-y divide-white/5">
            {outgoing.map((f) => (
              <div key={f.id} className="p-3 flex items-center gap-3" data-testid={`outgoing-${f.id}`}>
                <FriendAvatar u={f} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{f.peer_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{f.peer_member_id}</div>
                </div>
                <span className="chip text-white/40">Pending</span>
                <button onClick={() => remove(f.id)} className="btn-ghost !p-2 text-red-400">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary mb-3">Freunde · {accepted.length}</div>
        {accepted.length === 0 ? (
          <div className="border border-dashed border-white/10 p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">
            Noch keine Freunde — finde Mitglieder oben.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {accepted.map((f) => (
              <div key={f.id} className="card-solid p-4 flex items-center gap-3" data-testid={`friend-${f.id}`}>
                <FriendAvatar u={f} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{f.peer_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{f.peer_member_id}</div>
                </div>
                <button onClick={() => remove(f.id)} className="btn-ghost !p-2 text-red-400" data-testid={`unfriend-${f.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}
    </div>
  );
}

function FriendAvatar({ u }) {
  return u.peer_picture ? (
    <img src={u.peer_picture} alt="" className="w-9 h-9 object-cover rounded-sm border border-white/10" />
  ) : (
    <div className="w-9 h-9 bg-neon-gradient-soft border border-primary/30 text-primary flex items-center justify-center font-mono text-xs rounded-sm">
      {u.peer_name?.[0]?.toUpperCase()}
    </div>
  );
}
